import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { RotatingLines } from "react-loader-spinner";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import { showApiErrors } from "../../utils/errorHandler";

const AttendanceReport = () => {
  const { requestWithAuth } = useAuth();
  
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalMembers: 0,
    totalPresent: 0,
    totalAbsent: 0,
    totalUnmarked: 0,
    attendanceRate: 0
  });
  
  const [filters, setFilters] = useState({
    status: 'all', // all, present, absent, unmarked
    searchTerm: ''
  });

  // Set default date range (current week)
  useEffect(() => {
    const today = new Date();
    const currentDay = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - currentDay);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    setDateRange({
      startDate: startOfWeek.toISOString().split('T')[0],
      endDate: endOfWeek.toISOString().split('T')[0]
    });
  }, []);

  // Mock data for demonstration
  const generateMockData = () => {
    const dates = getDateRangeArray();
    if (dates.length === 0) return [];

    const mockMembers = [
      { id: 1, first_name: "John", last_name: "Smith", member_id: "MEM001", email: "john.smith@email.com" },
      { id: 2, first_name: "Mary", last_name: "Johnson", member_id: "MEM002", email: "mary.johnson@email.com" },
      { id: 3, first_name: "David", last_name: "Brown", member_id: "MEM003", email: "david.brown@email.com" },
      { id: 4, first_name: "Sarah", last_name: "Wilson", member_id: "MEM004", email: "sarah.wilson@email.com" },
      { id: 5, first_name: "Michael", last_name: "Davis", member_id: "MEM005", email: "michael.davis@email.com" },
      { id: 6, first_name: "Jennifer", last_name: "Miller", member_id: "MEM006", email: "jennifer.miller@email.com" },
      { id: 7, first_name: "Robert", last_name: "Garcia", member_id: "MEM007", email: "robert.garcia@email.com" },
      { id: 8, first_name: "Lisa", last_name: "Martinez", member_id: "MEM008", email: "lisa.martinez@email.com" },
      { id: 9, first_name: "William", last_name: "Anderson", member_id: "MEM009", email: "william.anderson@email.com" },
      { id: 10, first_name: "Amanda", last_name: "Taylor", member_id: "MEM010", email: "amanda.taylor@email.com" }
    ];

    const statuses = ['present', 'absent', 'unmarked'];
    
    return mockMembers.map(member => ({
      ...member,
      attendance_records: dates.map(date => ({
        date: date,
        status: Math.random() > 0.7 ? 'unmarked' : // 30% chance of unmarked
               Math.random() > 0.2 ? 'present' : 'absent', // 80% chance of present if marked, 20% absent
        marked_by: Math.random() > 0.5 ? "Admin User" : "Pastor Johnson",
        marked_at: `${date}T10:30:00Z`
      }))
    }));
  };

  // Fetch attendance report
  const fetchAttendanceReport = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    if (new Date(dateRange.startDate) > new Date(dateRange.endDate)) {
      toast.error("Start date cannot be after end date");
      return;
    }
    
    setLoading(true);
    
    try {
      

      const response = await requestWithAuth(
        `/members/attendance/report?start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`,
        'GET'
      );
      
      
      
      setAttendanceData(response?.data?.attendance_records || []);
      calculateSummary(response?.data?.attendance_records || []);
      toast.success("Attendance loaded successfully");
      
      
    } catch (error) {
      showApiErrors(error)
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary statistics
  const calculateSummary = (data) => {
    const totalMembers = data.length;
    let totalPresent = 0;
    let totalAbsent = 0;
    let totalUnmarked = 0;

    data.forEach(member => {
      const attendanceDays = member.attendance_records || [];
      attendanceDays.forEach(day => {
        if (day.status === 'present') totalPresent++;
        else if (day.status === 'absent') totalAbsent++;
        else totalUnmarked++;
      });
    });

    const attendanceRate = totalMembers > 0 ? ((totalPresent / (totalPresent + totalAbsent + totalUnmarked)) * 100).toFixed(1) : 0;

    setSummary({
      totalMembers,
      totalPresent,
      totalAbsent,
      totalUnmarked,
      attendanceRate
    });
  };

  // Handle date range change
  const handleDateChange = (e) => {
    setDateRange({
      ...dateRange,
      [e.target.name]: e.target.value
    });
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  // Filter attendance data
  const getFilteredData = () => {
    let filteredData = [...attendanceData];

    // Search filter
    if (filters.searchTerm) {
      filteredData = filteredData.filter(member =>
        `${member.first_name} ${member.last_name}`.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        member.member_id?.toLowerCase().includes(filters.searchTerm.toLowerCase())
      );
    }

    return filteredData;
  };

  // Get attendance status for a specific member and date
  const getAttendanceStatus = (member, date) => {
    const attendanceRecord = member.attendance_records?.find(record => record.date === date);
    return attendanceRecord?.status || 'unmarked';
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const baseClass = "px-2 py-1 rounded-full text-xs font-semibold";
    switch (status) {
      case 'present':
        return `${baseClass} bg-green-100 text-green-800`;
      case 'absent':
        return `${baseClass} bg-red-100 text-red-800`;
      case 'unmarked':
        return `${baseClass} bg-gray-100 text-gray-800`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800`;
    }
  };

  // Generate date range array
  const getDateRangeArray = () => {
    if (!dateRange.startDate || !dateRange.endDate) return [];
    
    const dates = [];
    const currentDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate).toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Export report to CSV
  const exportToCSV = () => {
    const dates = getDateRangeArray();
    const filteredData = getFilteredData();
    
    let csvContent = "Member ID,Name";
    dates.forEach(date => {
      csvContent += `,${new Date(date).toLocaleDateString()}`;
    });
    csvContent += "\n";
    
    filteredData.forEach(member => {
      csvContent += `${member.member_id || 'N/A'},"${member.first_name} ${member.last_name}"`;
      dates.forEach(date => {
        const status = getAttendanceStatus(member, date);
        csvContent += `,${status}`;
      });
      csvContent += "\n";
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `attendance_report_${dateRange.startDate}_to_${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const dates = getDateRangeArray();
  const filteredData = getFilteredData();

  return (
    <div className="p-4">
      <Header title="Attendance Report" />
      
      {/* Date Range Selection */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Select Date Range</h2>
        <div className="grid md:grid-cols-4 gap-4 items-end">
          <div className="flex flex-col">
            <label htmlFor="startDate" className="text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateChange}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          
          <div className="flex flex-col">
            <label htmlFor="endDate" className="text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateChange}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          
          <div className="flex flex-col">
            <label htmlFor="searchTerm" className="text-sm font-medium text-gray-700 mb-1">
              Search Members
            </label>
            <input
              type="text"
              id="searchTerm"
              name="searchTerm"
              placeholder="Search by name or ID..."
              value={filters.searchTerm}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={fetchAttendanceReport}
              disabled={loading}
              className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
            
            {attendanceData.length > 0 && (
              <button
                onClick={exportToCSV}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
              >
                Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      {attendanceData.length > 0 && (
        <div className="grid md:grid-cols-5 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{summary.totalMembers}</div>
            <div className="text-sm text-blue-800">Total Members</div>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{summary.totalPresent}</div>
            <div className="text-sm text-green-800">Present</div>
          </div>
          
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{summary.totalAbsent}</div>
            <div className="text-sm text-red-800">Absent</div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{summary.totalUnmarked}</div>
            <div className="text-sm text-gray-800">Unmarked</div>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{summary.attendanceRate}%</div>
            <div className="text-sm text-purple-800">Attendance Rate</div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <RotatingLines strokeColor="blue" height="50" width="50" />
          <span className="ml-3">Generating attendance report...</span>
        </div>
      )}

      {/* Attendance Table */}
      {!loading && attendanceData.length > 0 && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-4 bg-gray-50 border-b">
            <h3 className="text-lg font-semibold">
              Attendance Report ({new Date(dateRange.startDate).toLocaleDateString()} - {new Date(dateRange.endDate).toLocaleDateString()})
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Showing {filteredData.length} of {attendanceData.length} members
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                    Member
                  </th>
                  {dates.map(date => (
                    <th key={date} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                      <div>{new Date(date).toLocaleDateString('en-US', { weekday: 'short' })}</div>
                      <div>{new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Summary
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((member, index) => {
                  const memberPresent = dates.reduce((count, date) => {
                    return getAttendanceStatus(member, date) === 'present' ? count + 1 : count;
                  }, 0);
                  const memberAbsent = dates.reduce((count, date) => {
                    return getAttendanceStatus(member, date) === 'absent' ? count + 1 : count;
                  }, 0);
                  const memberUnmarked = dates.reduce((count, date) => {
                    return getAttendanceStatus(member, date) === 'unmarked' ? count + 1 : count;
                  }, 0);
                  const memberAttendanceRate = dates.length > 0 ? ((memberPresent / dates.length) * 100).toFixed(0) : 0;
                  
                  return (
                    <tr key={member.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-4 sticky left-0 bg-inherit z-10 border-r border-gray-200">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900">
                            {member.first_name} {member.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {member.member_id || 'N/A'}
                          </div>
                          <div className="text-xs text-blue-600 font-medium">
                            {memberAttendanceRate}% attendance
                          </div>
                        </div>
                      </td>
                      
                      {dates.map(date => {
                        const status = getAttendanceStatus(member, date);
                        const attendanceRecord = member.attendance_records?.find(record => record.date === date);
                        
                        return (
                          <td key={date} className="px-3 py-4 text-center">
                            <div className="flex flex-col items-center">
                              <span 
                                className={getStatusBadge(status)}
                                title={attendanceRecord ? 
                                  `${status.charAt(0).toUpperCase() + status.slice(1)}\nTime: ${new Date(attendanceRecord.marked_at).toLocaleTimeString()}` : 
                                  'Not marked'
                                }
                              >
                                {status === 'present' ? 'P' : status === 'absent' ? 'A' : '-'}
                              </span>
                              {attendanceRecord && status === 'present' && (
                                <div className="text-xs text-gray-400 mt-1">
                                  {new Date(attendanceRecord.marked_at).toLocaleTimeString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      
                      <td className="px-4 py-4 text-center border-l border-gray-200">
                        <div className="text-xs space-y-1">
                          <div className="flex items-center justify-center gap-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            <span className="text-green-600 font-medium">{memberPresent}</span>
                          </div>
                          <div className="flex items-center justify-center gap-1">
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            <span className="text-red-600 font-medium">{memberAbsent}</span>
                          </div>
                          <div className="flex items-center justify-center gap-1">
                            <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                            <span className="text-gray-600 font-medium">{memberUnmarked}</span>
                          </div>
                          <div className="text-xs text-blue-600 font-bold mt-1">
                            {memberAttendanceRate}%
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data State */}
      {!loading && attendanceData.length === 0 && dateRange.startDate && dateRange.endDate && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-gray-500 text-lg">No attendance data found for the selected date range.</div>
          <div className="text-gray-400 text-sm mt-2">
            Try selecting a different date range or ensure attendance has been marked for the selected period.
          </div>
        </div>
      )}

      {/* Legend */}
      {attendanceData.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-md p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Legend:</h4>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className={getStatusBadge('present')}>P</span>
              <span>Present</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={getStatusBadge('absent')}>A</span>
              <span>Absent</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={getStatusBadge('unmarked')}>-</span>
              <span>Not Marked</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceReport;